import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getRoles, createRole, updateRole, deleteRole } from '../services/api';
import { Plus, Edit2, Trash2, Shield, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const AVAILABLE_PAGES = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'products', name: 'Urunler' },
    { id: 'stock', name: 'Stok' },
    { id: 'fast-stock', name: 'Hizli Stok' },
    { id: 'sales', name: 'Satis' },
    { id: 'hr', name: 'HR' },
    { id: 'users', name: 'Kullanicilar' },
    { id: 'roles', name: 'Roller' },
    { id: 'settings', name: 'Ayarlar' },
];

const AVAILABLE_ACTIONS = {
    products: ['goruntule', 'ekle', 'duzenle', 'sil'],
    stock: ['goruntule', 'duzenle'],
    'fast-stock': ['goruntule', 'ekle'],
    sales: ['goruntule', 'ekle', 'duzenle', 'sil'],
    hr: ['goruntule', 'duzenle'],
    users: ['goruntule', 'ekle', 'duzenle', 'sil'],
    roles: ['goruntule', 'ekle', 'duzenle', 'sil'],
    settings: ['goruntule', 'duzenle'],
};

export default function Roles() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [expandedPages, setExpandedPages] = useState({});
    const [formData, setFormData] = useState({ name: '', permissions: { pages: [], actions: {} } });

    useEffect(() => { fetchRoles(); }, []);
    const fetchRoles = async () => {
        try { setRoles((await getRoles()).data); } catch { toast.error('Roller yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handleOpenModal = (role = null) => {
        if (role) { setEditingRole(role); setFormData({ name: role.name, permissions: role.permissions || { pages: [], actions: {} } }); }
        else { setEditingRole(null); setFormData({ name: '', permissions: { pages: ['dashboard'], actions: {} } }); }
        setExpandedPages({}); setShowModal(true);
    };

    const handlePageToggle = (pageId) => {
        setFormData(prev => {
            const pages = prev.permissions.pages.includes(pageId) ? prev.permissions.pages.filter(p => p !== pageId) : [...prev.permissions.pages, pageId];
            const actions = { ...prev.permissions.actions };
            if (!pages.includes(pageId)) delete actions[pageId];
            return { ...prev, permissions: { pages, actions } };
        });
    };

    const handleActionToggle = (pageId, action) => {
        setFormData(prev => {
            const pa = prev.permissions.actions[pageId] || [];
            return { ...prev, permissions: { ...prev.permissions, actions: { ...prev.permissions.actions, [pageId]: pa.includes(action) ? pa.filter(a => a !== action) : [...pa, action] } } };
        });
    };

    const handleSelectAllActions = (pageId) => {
        const all = AVAILABLE_ACTIONS[pageId] || [];
        const cur = formData.permissions.actions[pageId] || [];
        setFormData(prev => ({ ...prev, permissions: { ...prev.permissions, actions: { ...prev.permissions.actions, [pageId]: all.length === cur.length ? [] : [...all] } } }));
    };

    const toggleExpand = (pageId) => setExpandedPages(p => ({ ...p, [pageId]: !p[pageId] }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingRole) { await updateRole(editingRole.id, formData); toast.success('Rol guncellendi'); }
            else { await createRole(formData); toast.success('Rol olusturuldu'); }
            setShowModal(false); fetchRoles();
        } catch (error) { toast.error(error.response?.data?.detail || 'Rol kaydedilemedi'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Rolu silmek?')) return;
        try { await deleteRole(id); toast.success('Silindi'); fetchRoles(); } catch { toast.error('Silinemedi'); }
    };

    const getPageName = (pid) => AVAILABLE_PAGES.find(p => p.id === pid)?.name || pid;

    return (
        <DashboardLayout>
            <div className="space-y-5 max-w-5xl mx-auto" data-testid="roles-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="heading-2 mb-0.5">Roller</h1>
                        <p className="text-body text-sm">{roles.length} rol</p>
                    </div>
                    <button onClick={() => handleOpenModal()} className="btn btn-primary" data-testid="add-role-btn"><Plus size={16} /> Yeni Rol</button>
                </div>

                {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}</div>
                : roles.length > 0 ? (
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                        <table className="w-full mobile-cards" data-testid="roles-table">
                            <colgroup>
                                <col style={{ width: '30%' }} />
                                <col />
                                <col style={{ width: '90px' }} />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-[#E5E7EB]">
                                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Rol Adi</th>
                                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Sayfa Erisimleri</th>
                                    <th className="text-right px-4 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Islem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map(role => (
                                    <tr key={role.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid={`role-row-${role.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <Shield size={16} className="text-[#6366F1] flex-shrink-0" />
                                                <span className="font-semibold text-sm text-[#111827]">{role.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {role.permissions?.pages?.map(pid => (
                                                    <span key={pid} className="px-2 py-0.5 rounded bg-[#F3F4F6] text-[10px] font-medium text-[#374151]">{getPageName(pid)}</span>
                                                ))}
                                                {(!role.permissions?.pages?.length) && <span className="text-xs text-[#9CA3AF]">—</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <button onClick={() => handleOpenModal(role)} className="w-7 h-7 rounded-lg hover:bg-[#EFF6FF] flex items-center justify-center" data-testid={`edit-role-${role.id}`}><Edit2 size={14} className="text-[#3B82F6]" /></button>
                                                <button onClick={() => handleDelete(role.id)} className="w-7 h-7 rounded-lg hover:bg-[#FEF2F2] flex items-center justify-center" data-testid={`delete-role-${role.id}`}><Trash2 size={14} className="text-[#EF4444]" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] text-center py-14">
                        <Shield className="w-12 h-12 text-[#D1D5DB] mx-auto mb-2" />
                        <p className="text-sm text-[#9CA3AF] mb-4">Henuz rol yok</p>
                        <button onClick={() => handleOpenModal()} className="btn btn-primary"><Plus size={16} /> Yeni Rol</button>
                    </div>
                )}

                <Dialog open={showModal} onOpenChange={setShowModal}>
                    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" aria-label="Rol Formu">
                        <DialogHeader>
                            <DialogTitle data-testid="role-modal-title">{editingRole ? 'Rolu Duzenle' : 'Yeni Rol'}</DialogTitle>
                            <DialogDescription>{editingRole ? 'Izinleri guncelleyin.' : 'Yeni rol olusturun.'}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-5 mt-2" data-testid="role-form">
                            <div>
                                <label className="text-xs font-medium text-[#6B7280] block mb-1.5">Rol Adi *</label>
                                <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="input-apple" required data-testid="role-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6B7280] block mb-2">Sayfa Erisimleri</label>
                                <div className="space-y-1">
                                    {AVAILABLE_PAGES.map(page => {
                                        const isChecked = formData.permissions.pages.includes(page.id);
                                        const hasActions = AVAILABLE_ACTIONS[page.id];
                                        const isExpanded = expandedPages[page.id];
                                        const pageActions = formData.permissions.actions[page.id] || [];
                                        return (
                                            <div key={page.id}>
                                                <div className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors ${isChecked ? 'bg-[#EFF6FF]' : 'bg-[#F9FAFB] hover:bg-[#F3F4F6]'}`}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => handlePageToggle(page.id)} className="rounded" data-testid={`page-toggle-${page.id}`} />
                                                    <span className="text-sm font-medium text-[#111827] flex-1" onClick={() => handlePageToggle(page.id)}>{page.name}</span>
                                                    {isChecked && hasActions && <button type="button" onClick={() => toggleExpand(page.id)} className="p-0.5" data-testid={`expand-actions-${page.id}`}>{isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</button>}
                                                </div>
                                                {isChecked && hasActions && isExpanded && (
                                                    <div className="pl-9 pr-2 py-1.5 flex flex-wrap gap-1">
                                                        <button type="button" onClick={() => handleSelectAllActions(page.id)} className="text-[10px] text-[#3B82F6] mr-1" data-testid={`select-all-actions-${page.id}`}>{pageActions.length === AVAILABLE_ACTIONS[page.id].length ? 'Kaldir' : 'Hepsi'}</button>
                                                        {AVAILABLE_ACTIONS[page.id].map(action => (
                                                            <button key={action} type="button" onClick={() => handleActionToggle(page.id, action)}
                                                                className={`px-2 py-0.5 rounded text-[11px] font-medium ${pageActions.includes(action) ? 'bg-[#3B82F6] text-white' : 'bg-[#E5E7EB] text-[#374151]'}`}
                                                                data-testid={`action-${page.id}-${action}`}>{action}</button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary flex-1" data-testid="cancel-role-btn">Iptal</button>
                                <button type="submit" className="btn btn-primary flex-1" data-testid="save-role-btn">{editingRole ? 'Guncelle' : 'Olustur'}</button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
